import {
  Fragment,
  ReactNode,
  useState,
} from "react";
import { IGroupUpdate } from "shared";
import { EllipsisVerticalIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { When } from "react-if";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";

import { useCurrentGroup } from "../../../hooks/useGroups";
import { InputTextAddon } from "../../../../common/Atoms/InputTextAddon";
import { Button } from "../../../../common/Atoms/Button";
import { DropdownOption } from "../../../../common/Atoms/Dropdown";
import { Modal } from "../../../../common/Atoms/Modal";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { useDeleteGroupMutation, useUpdateGroupMutation } from "../../../services/api/groupApi/group";
import { classNames } from "../../../../common/lib/classNames";
import { Heading } from "../../../../common/Atoms/Typography/Heading";
import { Notification } from "../../../../common/Atoms/Notification";
import { validateForm } from "../../../services/forms/validate";

export function GroupHeading({ children }: { children?: ReactNode }) {
  const { group: savedGroup, groupFormData, onChange, onSave, updateLoading, isDirty } = useCurrentGroup();
  const [ deleteGroup ] = useDeleteGroupMutation();
  const [ updateGroup, { isLoading: isUpdatingDefaultGroup } ] = useUpdateGroupMutation();
  const [showEdit, setShowEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [makeDefaultError, setMakeDefaultError] = useState<string | null>(null);

  function getMutationErrorMessage(error: unknown): string {
    if (!error || typeof error !== `object`) {
      return `Failed to set default group.`;
    }

    const maybeError = error as {
      data?: unknown;
      error?: string;
    };

    if (typeof maybeError.error === `string` && maybeError.error) {
      return maybeError.error;
    }

    if (typeof maybeError.data === `string` && maybeError.data) {
      return maybeError.data;
    }

    if (
      maybeError.data &&
      typeof maybeError.data === `object` &&
      `message` in maybeError.data &&
      typeof maybeError.data.message === `string`
    ) {
      return maybeError.data.message;
    }

    return `Failed to set default group.`;
  }

  async function handleSave() {
    await onSave();

    setShowEdit(false);
  }

  async function onMakeDefault() {
    if (!savedGroup || savedGroup.default || isUpdatingDefaultGroup) {
      return;
    }

    setMakeDefaultError(null);

    const validateResult = await validateForm<IGroupUpdate>({
      ...savedGroup,
      default: true,
    }, `GROUP`);

    if (!validateResult.valid) {
      setMakeDefaultError(validateResult.invalids[0]?.message || `Failed to set default group.`);

      return;
    }

    try {
      await updateGroup({
        id: savedGroup.id,
        organisationId: savedGroup.organisationId,
        update: validateResult.form,
      }).unwrap();
    }
    catch (error) {
      setMakeDefaultError(getMutationErrorMessage(error));
    }
  }

  const dropdownOptions: DropdownOption[] = [];

  if (savedGroup && !savedGroup.default) {
    dropdownOptions.push({
      label: isUpdatingDefaultGroup ? `Setting as default...` : `Make Default`,
      onClick: onMakeDefault,
      disabled: isUpdatingDefaultGroup,
    });

    dropdownOptions.push({
      label: `Delete Group`,
      onClick: () => setConfirmDelete(true),
    });
  }

  function onDelete(e) {
    e?.preventDefault();

    deleteGroup({
      id: groupFormData.id,
      organisationId: groupFormData.organisationId,
    });
  }

  if (!groupFormData) {
    return null;
  }

  const headerActions = (
    <div className={ `flex items-center gap-2` }>
      <button
        onClick={ () => setShowEdit(true) }
        aria-label={ `Edit group title` }
      >
        <PencilSquareIcon className={ `h-5 w-5 text-paidniceGreen-600 hover:text-paidniceGreen-900` } />
      </button>

      <When condition={ !!dropdownOptions.length }>
        <Menu as={ `div` }
          className={ `relative flex-none` }>
          <MenuButton
            className={ `-m-2 block p-2 text-gray-500 hover:text-gray-900` }
            onClick={ e => {
              e.stopPropagation();

            } }>
            <span className={ `sr-only` }>{ `Open options` }</span>
            <EllipsisVerticalIcon className={ `h-5 w-5` }
              aria-hidden={ `true` } />
          </MenuButton>
          <Transition
            as={ Fragment }
            enter={ `transition ease-out duration-100` }
            enterFrom={ `transform opacity-0 scale-95` }
            enterTo={ `transform opacity-100 scale-100` }
            leave={ `transition ease-in duration-75` }
            leaveFrom={ `transform opacity-100 scale-100` }
            leaveTo={ `transform opacity-0 scale-95` }
          >
            <MenuItems className={ `absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-hidden` }>
              {
                dropdownOptions.map(option => (
                  <MenuItem key={ option.label }>
                    { ({ focus }) => (
                      <a
                        href={ `#` }
                        onClick={ e => {
                          e.preventDefault();
                          e.stopPropagation();

                          if (option.disabled || option.loading) {
                            return;
                          }

                          option.onClick();
                        } }
                        className={ classNames(
                          focus ? `bg-gray-50` : ``,
                          option.disabled ? `cursor-not-allowed text-gray-400` : ``,
                          `block px-3 py-1 text-sm leading-6`,
                        ) }
                      >
                        { option.label }
                      </a>
                    ) }
                  </MenuItem>
                ))
              }
            </MenuItems>
          </Transition>
        </Menu>
      </When>
    </div>
  );

  return (
    <>
      <Modal
        setOpen={ setConfirmDelete }
        open={ confirmDelete }
        title= { `Are you sure you want to delete this group?` }
      >
        <Paragraph>
          {
            `By deleting this group, any contacts assigned will be moved to the default group. Are you sure you want to delete this group?`
          }
        </Paragraph>

        <div className={ `flex justify-end space-x-2 mt-4` }>
          <Button
            onClick={ () => setConfirmDelete(false) }
            color={ `gray` }
          >
            { `Cancel` }
          </Button>
          <Button
            onClick={ onDelete }
            color={ `red` }
          >
            { `Delete Group` }
          </Button>
        </div>
      </Modal>

      <When condition={ !!makeDefaultError }>
        <div className={ `mb-2` }>
          <Notification type={ `error` }>
            { makeDefaultError }
          </Notification>
        </div>
      </When>

      {
        showEdit ? (
          <div>
            <div className={ `flex items-start space-x-2` }>
              <div>
                <InputTextAddon
                  required
                  value={ groupFormData.title }
                  onChange={ e => onChange(`title`, e.value) }
                  name={ `title` }
                  placeholder={ `Title` }
                />
                <InputTextAddon
                  value={ groupFormData.description || `` }
                  onChange={ e => onChange(`description`, e.value) }
                  name={ `description` }
                  placeholder={ `Description (optional)` }
                />
                <Paragraph
                  variant={ `help` }
                >
                  { `Give this group a title & description. Customers will not see this.` }
                </Paragraph>
              </div>
              <Button
                className={ `mt-2` }
                color={ `gray` }
                onClick={ () => setShowEdit(false) }
              >
                { `Cancel` }
              </Button>
              <Button
                className={ `mt-2` }
                onClick={ handleSave }
                loading={ updateLoading }
                disabled={ !isDirty || updateLoading }
              >
                { `Save` }
              </Button>
            </div>
            { children }
          </div>
        ) : (
          <div>
            <div className={ `flex items-start justify-between` }>
              <Heading>
                { groupFormData.title }
              </Heading>
              { headerActions }
            </div>
            <When condition={ !!groupFormData.description }>
              <div className={ `mt-2` }>
                <Notification
                  type={ `info` }
                  noIcon={ true }
                >
                  { groupFormData.description }
                </Notification>
              </div>
            </When>
            { children }
          </div>
        )
      }
    </>
  );
}
